"use client"

import { useState, useEffect } from "react"
import { getOrganizationStructure } from "api/apiHelper"

interface Title {
    id: number
    name: string
    level: {
      id: number
      code: string
    }
    department: {
      id: number
      name: string
    }
  }
  
  interface Employee {
    id: number
    first_name: string
    last_name: string
    title?: Title
    reports_to?: number | null
  }
  
  interface CurrentEmployee {
    id: string
    department?: string
    title?: Title
    levelCode?: string
    reports_to?: number | null
  }
  
  interface OrganizationStructureCardProps {
    currentEmployee: CurrentEmployee
    reportingChain: Employee[]
    permissions?: string[]
  }
  
  const OrganizationStructureCard = ({
    currentEmployee,
    permissions = [],
  }: OrganizationStructureCardProps) => {
    const [organizationData, setOrganizationData] = useState<Employee[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string>("")
  
    const currentEmployeeHasReportsTo = () => {
      // Check if the current employee has a reports_to relationship
      const currentEmp = organizationData.find(emp => emp.id.toString() === currentEmployee.id);
      return currentEmp?.reports_to != null || organizationData.length > 1;
    };
  
    useEffect(() => {
      const fetchOrganizationStructure = async () => {
        try {
          setIsLoading(true)
          const response = await getOrganizationStructure(currentEmployee.id)
          setOrganizationData(response.data || [])
          setError("")
        } catch (err: any) {
          console.error("Error fetching organization structure:", err)
          setError(err.message || "Failed to load organization structure")
        } finally {
          setIsLoading(false)
        }
      }
  
      fetchOrganizationStructure()
    }, [currentEmployee.id])
  
    const EmployeeNode = ({
      employee,
      isCurrentUser = false,
      level = 0,
    }: {
      employee: Employee | CurrentEmployee
      isCurrentUser?: boolean
      level?: number
    }) => {
      if (!employee) {
        return null;
      }
  
      const displayName = "first_name" in employee ? `${employee.first_name} ${employee.last_name}` : "Unknown Name"
  
      // Safe access to title properties with null checks - show "N/A" if not available
      const displayPosition = employee.title?.name || 'N/A'

      // Handle level code for both Employee and CurrentEmployee types - show "N/A" if not available
      const displayLevel = (() => {
        if ("title" in employee && employee.title?.level?.code) {
          return employee.title.level.code;
        }
        if ("levelCode" in employee && employee.levelCode) {
          return employee.levelCode;
        }
        return "N/A";
      })();
  
      const displayDepartment = "title" in employee && employee.title?.department?.name 
        ? employee.title.department.name 
        : "department" in employee && employee.department 
          ? employee.department 
          : ""
  
      const getLevelColor = (level: string) => {
        if (level === "N/A") {
          return isCurrentUser
            ? "bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-200"
            : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
        }
        
        const levelNum = Number.parseInt(level.replace("L", ""))
        if (levelNum <= 1)
          return isCurrentUser
            ? "bg-purple-100 dark:bg-purple-800 border-purple-300 dark:border-purple-600 text-purple-800 dark:text-purple-200"
            : "bg-purple-50 dark:bg-purple-900 border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300"
        if (levelNum <= 2)
          return isCurrentUser
            ? "bg-blue-100 dark:bg-blue-800 border-blue-300 dark:border-blue-600 text-blue-800 dark:text-blue-200"
            : "bg-blue-50 dark:bg-blue-900 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300"
        if (levelNum <= 5)
          return isCurrentUser
            ? "bg-green-100 dark:bg-green-800 border-green-300 dark:border-green-600 text-green-800 dark:text-green-200"
            : "bg-green-50 dark:bg-green-900 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300"
        return isCurrentUser
          ? "bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-200"
          : "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
      }
  
      return (
        <div
          className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
            isCurrentUser
              ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700"
              : "bg-gray-50 dark:bg-gray-700/50"
          }`}
        >
          <div className="relative">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-xs font-bold border-2 shadow-sm ${getLevelColor(displayLevel)}`}
            >
              {displayLevel}
            </div>
            {isCurrentUser && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full border-2 border-white dark:border-gray-800"></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm font-medium truncate ${
                isCurrentUser ? "text-blue-900 dark:text-blue-100" : "text-gray-900 dark:text-white"
              }`}
            >
              {displayName} {isCurrentUser && "(You)"}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{displayPosition}</p>
            {displayDepartment && (
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{displayDepartment}</p>
            )}
          </div>
        </div>
      )
    }
  
    const TreeConnector = ({ isLast = false }: { isLast?: boolean }) => (
      <div className="flex justify-center py-2">
        <div className="flex flex-col items-center">
          <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>
          <div className="w-3 h-3 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-full"></div>
          {!isLast && <div className="w-px h-4 bg-gray-300 dark:bg-gray-600"></div>}
        </div>
      </div>
    )
  
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-md overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
              <i className="fas fa-sitemap text-blue-600 dark:text-blue-400 mr-2"></i>
              Organization Structure
            </h2>
          </div>
  
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600 dark:text-gray-300">Loading organization structure...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="flex items-center text-red-600 dark:text-red-400">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {!currentEmployeeHasReportsTo() ? (
                <div className="text-center py-12">
                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-2xl p-8 border border-orange-200 dark:border-orange-700/50">
                    <div className="flex flex-col items-center space-y-4">
                      <div className="relative">
                        <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg">
                          <i className="fas fa-user-tie text-white text-2xl"></i>
                        </div>
                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center shadow-md">
                          <i className="fas fa-exclamation text-white text-sm"></i>
                        </div>
                      </div>
                      
                      <div className="text-center space-y-2">
                        <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200">
                          No Reporting Structure
                        </h3>
                        <p className="text-sm text-orange-600 dark:text-orange-300 max-w-md">
                          You currently do not have a reporting manager assigned. 
                          Please contact your HR department to set up your reporting structure.
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-xs text-orange-500 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30 px-4 py-2 rounded-full">
                        <i className="fas fa-info-circle"></i>
                        <span>Organization structure will appear once reporting is configured</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : organizationData.length > 0 ? (
                <>
                  {organizationData
                    .filter((employee) => {
                      // Skip employees with missing data
                      if (!employee || !employee.first_name || !employee.last_name) {
                        return false;
                      }
                      return true; // Include all valid employees (current user will be identified by ID)
                    })
                    .map((employee, index, filteredArray) => {
                      // Check if this employee is the current user based on ID
                      const isCurrentUser = employee.id.toString() === currentEmployee.id;
                      
                      return (
                        <div key={employee?.id || `employee-${index}`}>
                          <EmployeeNode 
                            employee={employee} 
                            isCurrentUser={isCurrentUser}
                            level={index} 
                          />
                          {index < filteredArray.length - 1 && <TreeConnector />}
                        </div>
                      );
                    })}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <i className="fas fa-users text-4xl mb-4"></i>
                  <p>No organization structure data available</p>
                </div>
              )}
            </div>
          )}
  
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
              <i className="fas fa-info-circle mr-2 text-blue-500"></i>
              <span>This shows your position in the organizational hierarchy</span>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  export default OrganizationStructureCard